/**
 * Utility functions for account-related operations
 * Focused on account data manipulation and validation
 */

/**
 * Determines the default payment type for an account
 */
export const getDefaultPaymentType = (account) => {
    const { fixed_payment, minimum_payment_percentage } = account;
    const minimumRequired = minimum_payment_percentage * account.balance;
    
    return (fixed_payment > 0 && fixed_payment >= minimumRequired) ? "fixed" : "minimum";
};

/**
 * Calculates the minimum fixed payment amount for an account
 */
export const getMinFixedAmount = (account) => {
    const { minimum_payment_percentage, minimum_payment_low_balance, balance } = account;
    
    const percentageAmount = minimum_payment_percentage * balance;
    return Math.max(percentageAmount, minimum_payment_low_balance);
};

/**
 * Gets the default payment amount based on account settings
 */
export const getDefaultPaymentAmount = (account) => {
    const paymentType = account.paymentType ?? getDefaultPaymentType(account);
    const minFixedAmount = getMinFixedAmount(account);

    if (paymentType === "fixed" && account.fixed_payment >= minFixedAmount) {
        return account.fixed_payment;
    }

    return minFixedAmount;
};

/**
 * Validates that all accounts have payment schedules
 */
export const checkAllAccountsForSchedule = (accounts) => {
    if (!accounts || accounts.length === 0) {
        return false;
    }
    
    return accounts.every(account => 
        account.paymentSchedule && 
        Array.isArray(account.paymentSchedule) && 
        account.paymentSchedule.length > 0
    );
};

/**
 * Adds default properties to an account for snowball calculations
 */
export const addDefaultAccountProperties = (account, index) => {
    return {
        ...account,
        index,
        paymentType: getDefaultPaymentType(account),
        minFixedAmount: getMinFixedAmount(account),
        defaultPaymentAmount: getDefaultPaymentAmount(account),
        paymentSchedule: null,
        snowballData: null,
        extraPayments: []
    };
};

/**
 * Sorts accounts by balance (debt snowball method)
 */
export const sortAccountsByBalance = (accounts) => {
    return [...accounts].sort((a, b) => a.balance - b.balance);
};

/**
 * Sorts accounts by interest rate (debt avalanche method)
 */
export const sortAccountsByInterestRate = (accounts) => {
    return [...accounts].sort((a, b) => b.apr - a.apr);
};

/**
 * Filters accounts that have positive balances
 */
export const filterAccountsWithBalance = (accounts) => {
    return accounts.filter(account => account.balance > 0);
};

/**
 * Validates account data for snowball calculations
 */
export const validateAccountForCalculation = (account) => {
    const errors = [];
    
    if (!account.balance || account.balance <= 0) {
        errors.push('Account must have a positive balance');
    }
    
    if (!account.apr && account.apr !== 0) {
        errors.push('Account must have an APR defined');
    }
    
    if (!account.minimum_payment_percentage) {
        errors.push('Account must have a minimum payment percentage');
    }
    
    if (!account.minimum_payment_low_balance) {
        errors.push('Account must have a minimum payment amount for low balances');
    }
    
    if (!account.due_date) {
        errors.push('Account must have a due date');
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Validates all accounts for snowball calculations
 */
export const validateAllAccountsForCalculation = (accounts) => {
    if (!accounts || accounts.length === 0) {
        return {
            isValid: false,
            errors: ['No accounts provided for calculation'],
            accountErrors: {}
        };
    }

    const accountErrors = {};
    let hasErrors = false;

    accounts.forEach(account => {
        const validation = validateAccountForCalculation(account);
        if (!validation.isValid) {
            accountErrors[account.id] = validation.errors;
            hasErrors = true;
        }
    });

    return {
        isValid: !hasErrors,
        errors: hasErrors ? ['One or more accounts have validation errors'] : [],
        accountErrors
    };
};